import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, MessageSquare, Send } from 'lucide-react';
import { StarRating, RatingSummary } from '../components/StarRating';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardReviewsPage() {
  const { token, user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  // Calculate stats
  const averageRating = reviews.length > 0 
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length 
    : 0;

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/dashboard/reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(res.data);
    } catch (error) {
      console.error('Error loading reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (reviewId) => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    try {
      setSubmittingReply(true);
      await axios.post(
        `${API_URL}/api/dashboard/reviews/${reviewId}/reply`,
        { reply: replyText.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reply added successfully');
      setReplyingTo(null);
      setReplyText('');
      loadReviews();
    } catch (error) {
      console.error('Error adding reply:', error);
      toast.error(error.response?.data?.detail || 'Failed to add reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const cancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Reviews</h2>
          <p className="text-muted-foreground">
            See what your clients are saying and respond to their feedback
          </p>
        </div>

        {/* Stats Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold">Your Rating</h3>
                <RatingSummary 
                  averageRating={Math.round(averageRating * 10) / 10} 
                  reviewCount={reviews.length}
                  size="lg"
                />
              </div>
              <div className="flex gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold text-primary">
                    {reviews.length}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Reviews</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-primary">
                    {averageRating > 0 ? averageRating.toFixed(1) : '-'}
                  </p>
                  <p className="text-sm text-muted-foreground">Avg Rating</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reviews List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : reviews.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No reviews yet</h3>
              <p className="text-muted-foreground mt-1">
                Your reviews will appear here once clients leave feedback after their completed bookings.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <Card key={review.id} data-testid={`review-${review.id}`}>
                <CardContent className="pt-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div>
                      <p className="font-semibold text-lg">{review.clientName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <StarRating rating={review.rating} size="md" />
                        <span className="font-medium">{review.rating}/5</span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(review.createdAt), 'MMMM d, yyyy')}
                    </p>
                  </div>
                  
                  <p className="mt-4 text-muted-foreground">{review.comment}</p>

                  {/* Provider Reply */}
                  {review.providerReply ? (
                    <div className="mt-4 p-4 bg-muted rounded-lg">
                      <p className="text-sm font-medium text-primary">Your Response:</p>
                      <p className="text-sm text-muted-foreground mt-1">{review.providerReply}</p>
                      {review.providerReplyAt && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Replied on {format(new Date(review.providerReplyAt), 'MMM d, yyyy')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="mt-4">
                      {replyingTo === review.id ? (
                        <div className="space-y-3">
                          <Textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Write your response..."
                            rows={3}
                            data-testid="reply-textarea"
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleReply(review.id)}
                              disabled={submittingReply}
                              data-testid="submit-reply-btn"
                            >
                              {submittingReply ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <Send className="mr-2 h-4 w-4" />
                                  Send Reply
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={cancelReply}
                              disabled={submittingReply}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setReplyingTo(review.id)}
                          data-testid="reply-btn"
                        >
                          <MessageSquare className="mr-2 h-4 w-4" />
                          Reply to Review
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
